import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import {
  CustomRoutes,
  localStorageStore,
  Resource,
  useGetIdentity,
} from "ra-core";
import { useEffect, useMemo } from "react";
import { Navigate, Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { Admin } from "@/components/admin/admin";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { LogsPage } from "../logs/LogsPage";
import { MapPage } from "../map/MapPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/turso";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import { waitForColumnPreferences } from "../misc/columnPreferenceWrites";
import {
  defaultClientChecklist,
  defaultClubChecklist,
  defaultRefereeChecklist,
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { ChangePasswordPage } from "../login/ChangePasswordPage.tsx";
import { SESSION_EVENT } from "../providers/turso/authProvider";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { ClientsPage } from "../clients/ClientsPage.tsx";
import { ClientChecklistSettingsPage } from "../settings/ClientChecklistSettings.tsx";
import { withContactTypes } from "../providers/commons/withContactTypes";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  clientChecklist = defaultClientChecklist,
  clubChecklist = defaultClubChecklist,
  refereeChecklist = defaultRefereeChecklist,
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  googleWorkplaceDomain = import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  const queryClient = useMemo(() => new QueryClient(), []);
  const typedDataProvider = useMemo(
    () => withContactTypes(dataProvider),
    [dataProvider],
  );

  useEffect(() => {
    localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
    localStorage.removeItem("app_auth");
    localStorage.removeItem("app_login_attempts");
    const clearCache = () => queryClient.clear();
    window.addEventListener(SESSION_EVENT, clearCache);
    window.addEventListener("storage", clearCache);
    return () => {
      window.removeEventListener(SESSION_EVENT, clearCache);
      window.removeEventListener("storage", clearCache);
    };
  }, [queryClient]);
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        clientChecklist,
        clubChecklist,
        refereeChecklist,
        companySectors,
        currency,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
        googleWorkplaceDomain,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        queryClient.clear();
        if ((result as any)?.redirectTo) return result;
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        await waitForColumnPreferences();
        queryClient.clear();
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, queryClient, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={typedDataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      queryClient={queryClient}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const ActiveLayout = props.layout ?? Layout;
  const GuardedLayout = (layoutProps: any) => (
    <PasswordChangeGuard layout={ActiveLayout} {...layoutProps} />
  );
  return (
    <Admin
      layout={GuardedLayout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route
          path={ClientChecklistSettingsPage.path}
          element={<ClientChecklistSettingsPage />}
        />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path={MapPage.path} element={<MapPage />} />
        <Route path={LogsPage.path} element={<LogsPage />} />
      </CustomRoutes>
      <Resource name="contacts" {...contacts} />
      <Resource name="partners" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="club_referees" />
      <Resource name="contact_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const ActiveLayout = props.layout ?? MobileLayout;
  const GuardedLayout = (layoutProps: any) => (
    <PasswordChangeGuard layout={ActiveLayout} {...layoutProps} />
  );
  return (
    <Admin
      layout={GuardedLayout}
      dashboard={props.dashboard ?? MobileDashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/clients" element={<ClientsPage />} />
        <Route
          path={ClientChecklistSettingsPage.path}
          element={<ClientChecklistSettingsPage />}
        />
        <Route
          path={SettingsPageMobile.path}
          element={<SettingsPageMobile />}
        />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path={MapPage.path} element={<MapPage />} />
      </CustomRoutes>
      <Resource
        name="contacts"
        list={ContactListMobile}
        show={ContactShow}
        recordRepresentation={contacts.recordRepresentation}
      >
        <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
      </Resource>
      <Resource
        name="partners"
        list={ContactListMobile}
        show={ContactShow}
        recordRepresentation={contacts.recordRepresentation}
      >
        <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
      </Resource>
      <Resource name="companies" {...companies} />
      <Resource name="club_referees" />
      <Resource name="tasks" list={MobileTasksList} />
      <Resource name="sales" {...sales} />
    </Admin>
  );
};

const PasswordChangeGuard = ({ layout: Layout, ...props }: any) => {
  const { identity, isPending } = useGetIdentity();
  if (isPending) return null;
  if (
    (identity as { must_change_password?: boolean } | undefined)
      ?.must_change_password
  ) {
    return <Navigate to="/change-password" replace />;
  }
  return <Layout {...props} />;
};
